/**
 * Alerts tab showing stale data, pending reviews, and action items.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertTriangle,
  Clock,
  FileCheck,
  RefreshCw,
  CheckCircle2,
  Mail,
} from "lucide-react";

interface AlertsTabProps {
  orgId: string | null;
  familyId?: string | null;
}

// Calculate staleness in days
function calculateStaleDays(latestDate: string | null): number {
  if (!latestDate) return 999;
  const latest = new Date(latestDate);
  const now = new Date();
  const diffMs = now.getTime() - latest.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

// Format date
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}

export function AlertsTab({ orgId, familyId }: AlertsTabProps) {
  // Fetch alt vehicles with latest metrics
  const { data: vehicles, isLoading: vehiclesLoading } = useQuery({
    queryKey: ["alt-vehicles-alerts", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];
      let query = supabase
        .from("alt_vehicles")
        .select("id, vehicle_name, manager_name")
        .eq("org_id", orgId);

      if (familyId) {
        query = query.eq("family_id", familyId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  // Fetch latest metrics for each vehicle
  const vehicleIds = vehicles?.map((v) => v.id) || [];
  const { data: metrics, isLoading: metricsLoading } = useQuery({
    queryKey: ["alt-metrics-alerts", vehicleIds],
    queryFn: async () => {
      if (vehicleIds.length === 0) return [];
      const { data, error } = await supabase
        .from("alt_period_metrics")
        .select("alt_id, period_end")
        .in("alt_id", vehicleIds)
        .order("period_end", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: vehicleIds.length > 0,
  });

  // Fetch documents pending review
  const { data: pendingDocs, isLoading: docsLoading } = useQuery({
    queryKey: ["pending-docs", orgId, familyId],
    queryFn: async () => {
      if (!orgId) return [];
      let query = supabase
        .from("documents")
        .select("id, type, metadata, created_at")
        .eq("org_id", orgId)
        .eq("status", "completed")
        .or("review_status.is.null,review_status.eq.pending_review")
        .order("created_at", { ascending: false });

      if (familyId) {
        query = query.eq("family_id", familyId);
      }

      const { data, error } = await query.limit(10);
      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  // Process stale funds
  const { staleFunds, pendingFunds } = useMemo(() => {
    if (!vehicles || !metrics) return { staleFunds: [], pendingFunds: [] };

    // Get latest metric date per vehicle
    const latestByVehicle = new Map<string, string>();
    metrics.forEach((m) => {
      const existing = latestByVehicle.get(m.alt_id);
      if (!existing || m.period_end > existing) {
        latestByVehicle.set(m.alt_id, m.period_end);
      }
    });

    const stale: Array<{ id: string; name: string; manager: string; days: number; lastDate: string | null }> = [];
    const pending: Array<{ id: string; name: string; manager: string; days: number; lastDate: string | null }> = [];

    vehicles.forEach((v) => {
      const lastDate = latestByVehicle.get(v.id) || null;
      const days = calculateStaleDays(lastDate);

      if (days > 90) {
        stale.push({
          id: v.id,
          name: v.vehicle_name,
          manager: v.manager_name,
          days,
          lastDate,
        });
      } else if (days > 60) {
        pending.push({
          id: v.id,
          name: v.vehicle_name,
          manager: v.manager_name,
          days,
          lastDate,
        });
      }
    });

    return {
      staleFunds: stale.sort((a, b) => b.days - a.days),
      pendingFunds: pending.sort((a, b) => b.days - a.days),
    };
  }, [vehicles, metrics]);

  const isLoading = vehiclesLoading || metricsLoading || docsLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const hasNoAlerts = staleFunds.length === 0 && pendingFunds.length === 0 && (pendingDocs?.length || 0) === 0;

  return (
    <div className="space-y-6">
      {hasNoAlerts && (
        <Card className="border-green-500/50 bg-green-50/30 dark:bg-green-900/10">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-green-700 dark:text-green-500">
              All Clear!
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              No stale data or pending items require your attention.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Stale Data (>90 days) */}
      {staleFunds.length > 0 && (
        <Card className="border-red-500/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <CardTitle>Stale Data ({staleFunds.length})</CardTitle>
            </div>
            <CardDescription>
              Funds with no updates in over 90 days - action required
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {staleFunds.map((fund) => (
                <div
                  key={fund.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50/50 dark:border-red-800 dark:bg-red-900/20"
                >
                  <div>
                    <div className="font-medium">{fund.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {fund.manager} • Last updated {fund.lastDate ? formatDate(fund.lastDate) : "Never"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">{fund.days}d</Badge>
                    <Button variant="outline" size="sm">
                      <Mail className="h-4 w-4 mr-1" />
                      Request Statement
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Review (61-90 days) */}
      {pendingFunds.length > 0 && (
        <Card className="border-yellow-500/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-600" />
              <CardTitle>Getting Stale ({pendingFunds.length})</CardTitle>
            </div>
            <CardDescription>
              Funds approaching 90-day staleness threshold
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingFunds.map((fund) => (
                <div
                  key={fund.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-yellow-200 bg-yellow-50/50 dark:border-yellow-800 dark:bg-yellow-900/20"
                >
                  <div>
                    <div className="font-medium">{fund.name}</div>
                    <div className="text-sm text-muted-foreground">
                      {fund.manager} • Last updated {fund.lastDate ? formatDate(fund.lastDate) : "Never"}
                    </div>
                  </div>
                  <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                    {fund.days}d
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Document Approvals */}
      {pendingDocs && pendingDocs.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-blue-600" />
              <CardTitle>Pending Approvals ({pendingDocs.length})</CardTitle>
            </div>
            <CardDescription>
              Documents waiting for review and approval
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingDocs.map((doc) => {
                const fundName = doc.metadata?.extracted_data?.fund_name || "Unknown Document";
                const docType = doc.type?.replace(/_/g, " ") || "Document";

                return (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div>
                      <div className="font-medium">{fundName}</div>
                      <div className="text-sm text-muted-foreground capitalize">
                        {docType} • Uploaded {formatDate(doc.created_at)}
                      </div>
                    </div>
                    <Button variant="default" size="sm" asChild>
                      <Link to={`/data/review/${doc.id}`}>
                        <FileCheck className="h-4 w-4 mr-1" />
                        Review
                      </Link>
                    </Button>
                  </div>
                );
              })}
            </div>
            {pendingDocs.length >= 10 && (
              <div className="mt-4 text-center">
                <Button variant="outline" asChild>
                  <Link to="/data/ingestion">View All Pending Documents</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Refresh Data Action */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="font-medium">Refresh All Data</div>
                <div className="text-sm text-muted-foreground">
                  Re-process all documents and recalculate metrics
                </div>
              </div>
            </div>
            <Button variant="outline">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
